import { useCallback, useEffect, useRef, useState } from "react";

import { WAVEFORM_BUCKETS } from "../domain/waveform";

export type RecorderError =
    /** The browser refused the microphone, or there is none. */
    | "microphone"
    /** The browser cannot record at all (Safari before 14.1, locked-down WebViews). */
    | "unsupported"
    | "failed";

export type MeditationRecording = {
    blob: Blob;
    /** What to tell the API the file is, so it is stored under the right name. */
    contentType: string;
    durationSeconds: number;
    /** For the preview player. Revoked when the recording is replaced. */
    url: string;
};

export type MeditationRecorder = {
    recording: boolean;
    paused: boolean;
    /** Seconds captured so far, so the therapist can see it running. */
    elapsedSeconds: number;
    /**
     * The take as it is being spoken: one level per slice, oldest first.
     *
     * A microphone that is not picking anything up looks exactly like one that
     * is, until the recording is played back — by which point four minutes are
     * gone. The lane draws these live so the therapist can see their own voice
     * arriving.
     */
    levels: number[];
    result: MeditationRecording | null;
    error: RecorderError | null;
    start: () => Promise<void>;
    pause: () => void;
    resume: () => void;
    stop: () => void;
    /** Throw the take away and start again — the common case, not an edge one. */
    discard: () => void;
};

/**
 * The formats worth asking for, best first.
 *
 * Opus in WebM is what Chrome and Firefox record and what the pipeline
 * already handles; Safari records MP4/AAC and nothing else. The list is tried
 * in order rather than trusting one, because `MediaRecorder` accepts a type it
 * cannot actually produce on some builds and silently falls back.
 */
/** How often the live lane gains a bar while recording. */
const LEVEL_EVERY_MS = 120;

const PREFERRED_TYPES = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/mp4",
    "audio/ogg;codecs=opus",
];

function pickMimeType(): string | null {
    if (typeof MediaRecorder === "undefined") return null;
    return (
        PREFERRED_TYPES.find((type) => MediaRecorder.isTypeSupported?.(type)) ??
        null
    );
}

/** The bare type, without the codec parameter the API does not want. */
export function baseContentType(mimeType: string): string {
    return mimeType.split(";")[0];
}

/**
 * Recording a therapist's voice in the browser.
 *
 * The voice alone: the bed is chosen after the take, in the studio, and laid
 * under it at playback. Baking it in meant deciding on the music before
 * hearing a word of the meditation, and re-recording to change your mind —
 * and, since the session lays the chosen bed under whatever it plays, the
 * same bed twice.
 */
export function useMeditationRecorder(): MeditationRecorder {
    const [recording, setRecording] = useState(false);
    const [paused, setPaused] = useState(false);
    const [elapsedSeconds, setElapsedSeconds] = useState(0);
    const [result, setResult] = useState<MeditationRecording | null>(null);
    const [levels, setLevels] = useState<number[]>([]);
    const [error, setError] = useState<RecorderError | null>(null);

    const recorderRef = useRef<MediaRecorder | null>(null);
    const chunksRef = useRef<Blob[]>([]);
    const micStreamRef = useRef<MediaStream | null>(null);
    const contextRef = useRef<AudioContext | null>(null);
    const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);
    // The live meter: an analyser on the same graph the recorder captures, and
    // the animation frame reading it.
    const analyserRef = useRef<AnalyserNode | null>(null);
    const meterRef = useRef<number | null>(null);
    // The length, readable from `onstop` — a callback created before the take
    // began, which would otherwise close over an elapsed count of zero.
    const elapsedRef = useRef(0);
    // The preview URL currently handed out, so it can be revoked when the next
    // take replaces it rather than leaking a blob per attempt.
    const previewUrlRef = useRef<string | null>(null);

    const stopTicking = useCallback(() => {
        if (tickRef.current) {
            clearInterval(tickRef.current);
            tickRef.current = null;
        }
    }, []);

    /**
     * Stop drawing, keeping the analyser.
     *
     * Pausing stops the bars but not the graph they are read from: the take is
     * still open, and resuming has to carry on drawing into the same lane
     * rather than into nothing.
     */
    const stopMeter = useCallback(() => {
        if (meterRef.current !== null) {
            cancelAnimationFrame(meterRef.current);
            meterRef.current = null;
        }
    }, []);

    /** Read the live level into the lane, from wherever it left off. */
    const startMeter = useCallback(() => {
        const analyser = analyserRef.current;
        if (!analyser || meterRef.current !== null) return;

        const samples = new Uint8Array(analyser.fftSize);
        let lastAdded = 0;
        const readLevel = (now: number) => {
            meterRef.current = requestAnimationFrame(readLevel);
            const current = analyserRef.current;
            if (!current) return;
            // A bar every so often rather than every frame: the lane holds a
            // fixed number of them, and sixty a second would scroll a whole
            // take past in under two.
            if (now - lastAdded < LEVEL_EVERY_MS) return;
            lastAdded = now;

            current.getByteTimeDomainData(samples);
            let peak = 0;
            for (let i = 0; i < samples.length; i += 1) {
                // 128 is silence in this encoding; the distance from it is the
                // amplitude.
                const level = Math.abs(samples[i] - 128) / 128;
                if (level > peak) peak = level;
            }
            setLevels((bars) => [...bars, peak].slice(-WAVEFORM_BUCKETS));
        };
        meterRef.current = requestAnimationFrame(readLevel);
    }, []);

    /** Put down everything the capture is holding: the device and the graph. */
    const teardown = useCallback(() => {
        stopTicking();
        stopMeter();
        analyserRef.current = null;
        micStreamRef.current?.getTracks().forEach((track) => track.stop());
        micStreamRef.current = null;

        const context = contextRef.current;
        contextRef.current = null;
        // Closing returns a promise that rejects if the context is already
        // closed, which is not something the therapist needs to hear about.
        void context?.close().catch(() => undefined);
    }, [stopMeter, stopTicking]);

    const start = useCallback(async () => {
        setError(null);

        const mimeType = pickMimeType();
        if (!mimeType || typeof navigator.mediaDevices?.getUserMedia !== "function") {
            setError("unsupported");
            return;
        }

        let micStream: MediaStream;
        try {
            micStream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    echoCancellation: true,
                    noiseSuppression: true,
                },
            });
        } catch {
            setError("microphone");
            return;
        }
        micStreamRef.current = micStream;

        try {
            const AudioContextCtor =
                window.AudioContext ??
                (window as unknown as { webkitAudioContext?: typeof AudioContext })
                    .webkitAudioContext;
            const context = new AudioContextCtor();
            contextRef.current = context;

            const destination = context.createMediaStreamDestination();
            const source = context.createMediaStreamSource(micStream);
            source.connect(destination);

            // Read off the same signal that is being recorded, so what the
            // therapist watches is what is going into the file.
            const analyser = context.createAnalyser();
            analyser.fftSize = 2048;
            source.connect(analyser);
            analyserRef.current = analyser;

            const recorder = new MediaRecorder(destination.stream, { mimeType });
            chunksRef.current = [];
            recorderRef.current = recorder;

            recorder.ondataavailable = (event) => {
                if (event.data.size > 0) chunksRef.current.push(event.data);
            };

            recorder.onstop = () => {
                const blob = new Blob(chunksRef.current, { type: mimeType });
                chunksRef.current = [];

                if (previewUrlRef.current) {
                    URL.revokeObjectURL(previewUrlRef.current);
                }
                const url = URL.createObjectURL(blob);
                previewUrlRef.current = url;

                // Counted rather than read back: the browser reports no
                // duration for a stream it recorded, and an `<audio>` element
                // loading a WebM blob says `Infinity` until it has been seeked
                // to the end.
                setResult({
                    blob,
                    contentType: baseContentType(mimeType),
                    durationSeconds: elapsedRef.current,
                    url,
                });
                setRecording(false);
                setPaused(false);
                teardown();
            };

            recorder.start(1000);
            setResult(null);
            elapsedRef.current = 0;
            setElapsedSeconds(0);
            setRecording(true);
            setPaused(false);

            tickRef.current = setInterval(() => {
                elapsedRef.current += 1;
                setElapsedSeconds(elapsedRef.current);
            }, 1000);

            setLevels([]);
            startMeter();
        } catch {
            teardown();
            setError("failed");
        }
    }, [startMeter, teardown]);

    const pause = useCallback(() => {
        const recorder = recorderRef.current;
        if (!recorder || recorder.state !== "recording") return;
        recorder.pause();
        stopTicking();
        stopMeter();
        setPaused(true);
    }, [stopMeter, stopTicking]);

    const resume = useCallback(() => {
        const recorder = recorderRef.current;
        if (!recorder || recorder.state !== "paused") return;
        recorder.resume();
        setPaused(false);
        // The lane picks up where it stopped rather than starting again: the
        // bars already drawn are part of this take.
        startMeter();
        tickRef.current = setInterval(() => {
            elapsedRef.current += 1;
            setElapsedSeconds(elapsedRef.current);
        }, 1000);
    }, [startMeter]);

    const stop = useCallback(() => {
        const recorder = recorderRef.current;
        if (!recorder || recorder.state === "inactive") return;
        // `onstop` is where the take becomes a result — it is the only place
        // that has the bytes — so nothing is published here.
        recorder.stop();
        stopTicking();
    }, [stopTicking]);

    const discard = useCallback(() => {
        setLevels([]);
        if (previewUrlRef.current) {
            URL.revokeObjectURL(previewUrlRef.current);
            previewUrlRef.current = null;
        }
        setResult(null);
        elapsedRef.current = 0;
        setElapsedSeconds(0);
        setError(null);
    }, []);

    // Leaving the page mid-take should not leave the microphone light on.
    useEffect(
        () => () => {
            const recorder = recorderRef.current;
            if (recorder && recorder.state !== "inactive") recorder.stop();
            teardown();
            if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
        },
        [teardown]
    );

    return {
        recording,
        paused,
        elapsedSeconds,
        levels,
        result,
        error,
        start,
        pause,
        resume,
        stop,
        discard,
    };
}
